import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

export function FlutterHeartGame({ user, profile, onNavigate }) {
  const navigate = useNavigate();
      const canvasRef = useRef(null);
      const [score, setScore] = useState(0);
      const [gameOver, setGameOver] = useState(false);
      const [scoreSaved, setScoreSaved] = useState(false);
      const [restartTrigger, setRestartTrigger] = useState(0);
      const [settingsOpen, setSettingsOpen] = useState(false);
      const settingsRef = useRef(null);
      const gameRef = useRef({
        started: false,
        over: false,
        score: 0,
        bird: { y: 150, velocity: 0 },
        pipes: [],
        pipeTimer: 0,
        animationId: null,
      });
    
      useEffect(() => {
        const handleClickOutside = (e) => {
          if (settingsRef.current && !settingsRef.current.contains(e.target)) {
            setSettingsOpen(false);
          }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }, []);
    
      // Auto-save score to leaderboard when game ends
      useEffect(() => {
        if (!gameOver) return;
        setScoreSaved(false);
        supabase.from('leaderboard').insert([{
          user_id: user.id,
          score: gameRef.current.score,
          username: profile?.username || null,
          email: user.email,
        }]).then(({ error }) => {
          if (error) console.error('Error saving score:', error);
          setScoreSaved(true);
        });
      }, [gameOver, user, profile]);
    
      useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
    
        gameRef.current = {
          started: false,
          over: false,
          score: 0,
          bird: { y: 150, velocity: 0 },
          pipes: [],
          pipeTimer: 0,
          animationId: null,
          bgOffset: 0,
          flapAngle: 0,
          deathAnim: null,
        };
    
        const ctx = canvas.getContext('2d');
        const game = gameRef.current;
    
        const GRAVITY = 0.5;
        const FLAP = -10;
        const BIRD_X = 40;
        const BIRD_SIZE = 20;
        const PIPE_WIDTH = 80;
        const PIPE_GAP = 200;
        const PIPE_SPEED = 4;
    
        const drawCloud = (cx, cy, scale) => {
          ctx.beginPath();
          ctx.arc(cx,          cy,          scale * 18, 0, Math.PI * 2);
          ctx.arc(cx + scale * 22, cy - scale * 8,  scale * 14, 0, Math.PI * 2);
          ctx.arc(cx - scale * 20, cy - scale * 6,  scale * 13, 0, Math.PI * 2);
          ctx.arc(cx + scale * 10, cy - scale * 16, scale * 12, 0, Math.PI * 2);
          ctx.fill();
        };
    
        const drawCloudObstacle = (pipe) => {
          ctx.fillStyle = '#fff';
          ctx.shadowColor = 'rgba(180,150,220,0.25)';
          ctx.shadowBlur = 12;
    
          // Top cloud mass — fill from ceiling down, bumps on bottom edge
          if (pipe.top > 0) {
            ctx.fillRect(pipe.x - 8, 0, PIPE_WIDTH + 16, pipe.top);
            const steps = 3;
            const step = (PIPE_WIDTH + 16) / steps;
            for (let i = 0; i <= steps; i++) {
              ctx.beginPath();
              ctx.arc(pipe.x - 8 + i * step, pipe.top, 16, 0, Math.PI * 2);
              ctx.fill();
            }
          }
    
          // Bottom cloud mass — fill from bottom up, bumps on top edge
          if (pipe.bottom < canvas.height) {
            ctx.fillRect(pipe.x - 8, pipe.bottom, PIPE_WIDTH + 16, canvas.height - pipe.bottom);
            const steps = 3;
            const step = (PIPE_WIDTH + 16) / steps;
            for (let i = 0; i <= steps; i++) {
              ctx.beginPath();
              ctx.arc(pipe.x - 8 + i * step, pipe.bottom, 16, 0, Math.PI * 2);
              ctx.fill();
            }
          }
    
          ctx.shadowBlur = 0;
        };
    
        const render = () => {
          // Sky gradient
          const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
          grad.addColorStop(0, '#fce4f5');
          grad.addColorStop(1, '#fef0ff');
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
    
          // Decorative background clouds (slow scroll)
          game.bgOffset = (game.bgOffset + 0.4) % (canvas.width + 120);
          const bgCloudDefs = [
            { x: 60,  y: 80,  s: 0.7 },
            { x: 220, y: 200, s: 0.5 },
            { x: 370, y: 120, s: 0.6 },
            { x: 490, y: 300, s: 0.45 },
          ];
          ctx.fillStyle = 'rgba(255,255,255,0.55)';
          bgCloudDefs.forEach(c => {
            const x = ((c.x - game.bgOffset + canvas.width + 120) % (canvas.width + 120)) - 60;
            drawCloud(x, c.y, c.s);
          });
    
          // Cloud obstacles
          game.pipes.forEach(pipe => drawCloudObstacle(pipe));
    
          // Heart with wings
          const hx = BIRD_X + BIRD_SIZE / 2;
          const hy = game.bird.y + BIRD_SIZE / 2;
          const s = BIRD_SIZE * 0.62;
          const tilt = Math.min(Math.max(game.bird.velocity * 0.035, -0.4), 0.7);
          game.flapAngle += 0.14;
          const flap = Math.sin(game.flapAngle) * s * 0.45;
    
          ctx.save();
          ctx.translate(hx, hy);
          ctx.rotate(tilt);
    
          const drawWing = (side) => {
            const d = side;
            const wGrad = ctx.createLinearGradient(d * s * 0.3, 0, d * s * 2.2, 0);
            wGrad.addColorStop(0, 'rgba(255,220,240,1)');
            wGrad.addColorStop(0.5, 'rgba(255,240,250,0.97)');
            wGrad.addColorStop(1, 'rgba(255,255,255,0.85)');
            ctx.fillStyle = wGrad;
            ctx.beginPath();
            ctx.moveTo(d * s * 0.28, s * 0.05);
            ctx.bezierCurveTo(d * s * 0.9,  -s * 0.35 + flap,
                              d * s * 2.05, -s * 0.15 + flap,
                              d * s * 2.2,   s * 0.28 + flap * 0.3);
            ctx.bezierCurveTo(d * s * 1.7,  s * 0.55,
                              d * s * 0.75, s * 0.38,
                              d * s * 0.28, s * 0.28);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = 'rgba(230,170,210,0.55)';
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.strokeStyle = 'rgba(210,150,200,0.35)';
            ctx.lineWidth = 0.9;
            [[d*s*2.0, -s*0.1+flap*0.9], [d*s*1.5, -s*0.2+flap*0.7],
             [d*s*1.0, -s*0.25+flap*0.5], [d*s*0.6, -s*0.18+flap*0.3]].forEach(([fx, fy]) => {
              ctx.beginPath();
              ctx.moveTo(d * s * 0.28, s * 0.18);
              ctx.quadraticCurveTo(d * s * 0.5, fy * 0.5, fx, fy);
              ctx.stroke();
            });
          };
    
          if (!game.deathAnim) {
            drawWing(-1);
            drawWing(1);
          }
    
          // Heart emoji on top of wings
          // Draw heart as canvas shape — reliable on all devices including iOS
        const drawHeartPath = (size) => {
            ctx.moveTo(0, size * 0.4);
            ctx.bezierCurveTo(-size * 0.1,  size * 0.2,  -size * 0.65, -size * 0.1, -size * 0.65, -size * 0.15);
            ctx.bezierCurveTo(-size * 0.65, -size * 0.55, -size * 0.1,  -size * 0.65, 0,            -size * 0.35);
            ctx.bezierCurveTo( size * 0.1,  -size * 0.65,  size * 0.65, -size * 0.55, size * 0.65, -size * 0.15);
            ctx.bezierCurveTo( size * 0.65, -size * 0.1,   size * 0.1,   size * 0.2,  0,            size * 0.4);
            ctx.closePath();
            };
    
            const drawHeart = (x, y, size, color = '#f43f5e') => {
            ctx.save();
            ctx.translate(x, y);
            ctx.fillStyle = color;
            ctx.beginPath();
            drawHeartPath(size);
            ctx.fill();
            ctx.restore();
            };
    
            // Zig-zag crack line (defined in heart-local coords)
            const getZigZag = (size) => [
            { x:  size * 0.05,  y: -size * 0.65 },  // top entry
            { x: -size * 0.12,  y: -size * 0.35 },
            { x:  size * 0.1,   y: -size * 0.1  },
            { x: -size * 0.08,  y:  size * 0.15 },
            { x:  size * 0.06,  y:  size * 0.4  },  // bottom exit
            ];
    
            if (!game.deathAnim) {
            drawHeart(0, 0, BIRD_SIZE * 1.3);
            } else {
            const t = game.deathAnim.frame / 40;
            const spread = t * s * 2.5;
            const drop = t * t * s * 3;
            const spin = t * 1.0;
            const alpha = Math.max(0, 1 - t);
            const heartSize = BIRD_SIZE * 1.3;
            const zz = getZigZag(heartSize);
            ctx.globalAlpha = alpha;
    
            // Build zig-zag clip path for left half
            const leftClip = () => {
                ctx.beginPath();
                ctx.moveTo(zz[0].x, zz[0].y);
                zz.forEach(p => ctx.lineTo(p.x, p.y));
                ctx.lineTo(-heartSize * 2, zz[zz.length - 1].y);
                ctx.lineTo(-heartSize * 2, -heartSize * 2);
                ctx.lineTo(zz[0].x, -heartSize * 2);
                ctx.closePath();
                ctx.clip();
            };
    
            // Build zig-zag clip path for right half
            const rightClip = () => {
                ctx.beginPath();
                ctx.moveTo(zz[0].x, zz[0].y);
                zz.forEach(p => ctx.lineTo(p.x, p.y));
                ctx.lineTo(heartSize * 2, zz[zz.length - 1].y);
                ctx.lineTo(heartSize * 2, -heartSize * 2);
                ctx.lineTo(zz[0].x, -heartSize * 2);
                ctx.closePath();
                ctx.clip();
            };
    
            // Left half flies left
            ctx.save();
            ctx.translate(-spread, drop);
            ctx.rotate(-spin);
            leftClip();
            ctx.fillStyle = '#f43f5e';
            ctx.beginPath();
            drawHeartPath(heartSize);
            ctx.fill();
            ctx.restore();
    
            // Right half flies right
            ctx.save();
            ctx.translate(spread, drop);
            ctx.rotate(spin);
            rightClip();
            ctx.fillStyle = '#f43f5e';
            ctx.beginPath();
            drawHeartPath(heartSize);
            ctx.fill();
            ctx.restore();
    
            ctx.globalAlpha = 1;
            }
    
          ctx.restore();
    
          // Shockwave ring on first few frames of death
          if (game.deathAnim && game.deathAnim.frame < 20) {
            const t = game.deathAnim.frame / 20;
            ctx.save();
            ctx.translate(game.deathAnim.x, game.deathAnim.y);
            ctx.strokeStyle = `rgba(244,63,94,${1 - t})`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(0, 0, t * BIRD_SIZE * 4, 0, Math.PI * 2);
            ctx.stroke();
            ctx.restore();
          }
    
          // Score
          ctx.fillStyle = 'rgba(180,80,120,0.75)';
          ctx.font = 'bold 22px Satisfy, system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(game.score, canvas.width / 2, 36);
          ctx.textAlign = 'left';
    
          // Start message
          if (!game.started) {
            ctx.fillStyle = 'rgba(255,240,250,0.65)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#a03070';
            ctx.font = '600 16px Inter, system-ui, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Click or press Space to start', canvas.width / 2, canvas.height / 2);
            ctx.textAlign = 'left';
          }
        };
    
        const update = () => {
          if (!game.started || game.over) return;
    
          game.bird.velocity += GRAVITY;
          game.bird.y += game.bird.velocity;
    
          if (game.bird.y < 0 || game.bird.y + BIRD_SIZE > canvas.height) {
            game.over = true;
            setGameOver(true);
            return;
          }
    
          game.pipes.forEach(pipe => {
            pipe.x -= PIPE_SPEED;
    
            // Cloud bumps extend 16px inward into the gap, and 8px outward on each side
            const BUMP_R = 16;
            const CLOUD_EXTRA = 8;
            if (
              BIRD_X + BIRD_SIZE > pipe.x - CLOUD_EXTRA &&
              BIRD_X < pipe.x + PIPE_WIDTH + CLOUD_EXTRA &&
              (game.bird.y < pipe.top + BUMP_R || game.bird.y + BIRD_SIZE > pipe.bottom - BUMP_R)
            ) {
              game.over = true;
              game.deathAnim = { frame: 0, x: BIRD_X + BIRD_SIZE / 2, y: game.bird.y + BIRD_SIZE / 2 };
            }
    
            if (!pipe.scored && pipe.x + PIPE_WIDTH < BIRD_X) {
              pipe.scored = true;
              game.score += 1;
              setScore(game.score);
            }
          });
    
          game.pipes = game.pipes.filter(p => p.x > -PIPE_WIDTH);
    
          game.pipeTimer++;
          if (game.pipeTimer > 90) {
            const topHeight = 50 + Math.random() * (canvas.height - 150 - PIPE_GAP);
            game.pipes.push({ x: canvas.width, top: topHeight, bottom: topHeight + PIPE_GAP, scored: false });
            game.pipeTimer = 0;
          }
        };
    
        const DEATH_FRAMES = 40;
    
        const gameLoop = () => {
          update();
          render();
          if (!game.over) {
            game.animationId = requestAnimationFrame(gameLoop);
          } else if (game.deathAnim && game.deathAnim.frame <= DEATH_FRAMES) {
            game.deathAnim.frame++;
            render();
            if (game.deathAnim.frame <= DEATH_FRAMES) {
              game.animationId = requestAnimationFrame(gameLoop);
            } else {
              setGameOver(true);
            }
          }
        };
    
        const flap = () => {
          if (!game.started) { game.started = true; }
          else if (!game.over) { game.bird.velocity = FLAP; }
        };
    
        const handleKeyDown = (e) => { if (e.code === 'Space') { e.preventDefault(); flap(); } };
        const handleClick = () => flap();

        render();
        gameLoop();
        window.addEventListener('keydown', handleKeyDown);
        canvas.addEventListener('click', handleClick);
        canvas.addEventListener('touchstart', (e) => {
          e.preventDefault();   // kills the 300ms click delay on iOS
          flap();
        }, { passive: false });
        const handleTouch = (e) => { e.preventDefault(); flap(); };
        canvas.addEventListener('touchstart', handleTouch, { passive: false });
        // ↑ move the handler to a named const so you can remove it cleanly

        return () => {
          window.removeEventListener('keydown', handleKeyDown);
          canvas.removeEventListener('click', handleClick);
          canvas.removeEventListener('touchstart', handleTouch);
          cancelAnimationFrame(game.animationId);
        };
      }, [restartTrigger]);
    
      const handlePlayAgain = () => {
        setScore(0);
        setGameOver(false);
        setRestartTrigger(t => t + 1);
      };
    
      return (
        <div className="game-container">
          <div className="game-header">
            <div className="settings-menu" ref={settingsRef}>
              <button className="settings-btn" onClick={() => setSettingsOpen(v => !v)} aria-label="Settings">⚙️</button>
              {settingsOpen && (
                <div className="settings-dropdown">
                  <div className="settings-email">{user.email}</div>
                  <hr className="settings-divider" />
                  <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('edit-profile'); }}>Edit Username</button>
                  {profile?.is_admin && (
                    <button className="settings-item" onClick={() => { setSettingsOpen(false); onNavigate('admin'); }}>Admin Panel</button>
                  )}
                  <hr className="settings-divider" />
                  <button className="settings-item settings-item-danger" onClick={() => supabase.auth.signOut()}>Logout</button>
                </div>
              )}
            </div>
            <span className="game-username">{profile?.username || user.email}</span>
            <button className="btn back-btn" onClick={() => navigate('/')}>← Games</button>
          </div>

          <h1 className="game-title">🩷Flutter Heart</h1>
          <canvas ref={canvasRef} width={500} height={600} className="game-canvas" />
    
          <button className="btn leaderboard-btn" onClick={() => onNavigate('leaderboard')}>
            🏆 Leaderboard
          </button>
          <button className="btn leaderboard-btn" onClick={() => onNavigate('leagues')}>
            🏅 Leagues
          </button>
    
          {gameOver && (
            <div className="game-over-modal">
              <div className="game-over-box">
                <h2>Game Over!</h2>
                <p className="final-score">{score}</p>
                <button onClick={handlePlayAgain} className="btn">Play Again</button>
                <button onClick={() => onNavigate('leaderboard')} className="btn btn-submit" disabled={!scoreSaved}>
                  {scoreSaved ? 'View Leaderboard' : 'Saving…'}
                </button>
              </div>
            </div>
          )}
        </div>
      );
}